import { useQuery } from '@tanstack/react-query';
import type { AxiosError, AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { PaginationSchemaV2 } from 'types/index';
import { z } from 'zod/v4';

const LightProductSchema = z.object({
  id: z.coerce.string(),
  name: z.string(),
  description: z.string().nullable(),
  priceHtva: z.coerce.number(),
  vatRate: z.coerce.number(),
  taxCategoryCode: z.string(),
  isArchived: z.boolean(),
});

const ProductsSchema = z
  .object({
    data: z.array(LightProductSchema),
    pagination: PaginationSchemaV2,
  })
  .default({
    data: [],
    pagination: {
      totalItems: 0,
    },
  });

export type Products = z.infer<typeof ProductsSchema>;
export type LightProduct = z.infer<typeof LightProductSchema>;

export type ProductFilters = {
  page?: number;
  pageLimit?: number;
  sortOrder?: string;
  name?: string;
  isArchived?: boolean;
};

export async function loadProductsQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  { queryKey }: any,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get('/products', { params: queryKey[1] });

  const safeParse = ProductsSchema.safeParse(data);

  if (!safeParse.success) {
    handleZodError(safeParse, 'products');
    throw new SilentError();
  }

  return safeParse.data;
}

const emptyProducts: Products = ProductsSchema.parse(undefined);

export function useLoadProducts(params?: ProductFilters, options: QueryOptions<Products> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<Products, ApiError>({
    queryKey: ['products', params],
    queryFn: context => loadProductsQueryFn(axiosInstance, context),
    placeholderData: emptyProducts,
    ...options,
  });

  return addResourceNameToQueryResult<Products, AxiosError, 'products', 'PreventUndefined'>(
    'products',
    queryResult,
    emptyProducts,
  );
}
